import React, { useEffect } from 'react';
import { t } from 'i18next';
import { bool, func, oneOf, string } from 'prop-types';
import cn from 'classnames';

import styles from '../../styles.module.scss';

import { paymentMethodsPropTypes } from '~propTypes/subsidiary';
import { PAYMENT_METHODS, PAYMENT_METHODS_INFO, PAYMENT_METHODS_SERVER } from '~constants/paymentMethods';
import { checkoutPaymentMethodViaCookie } from '~utils/cookies';
import { COUNTRIES, CURRENT_COUNTRY_ID } from '~constants/environment';
import { isString } from '~utils/string';
import { getPaymentMethod } from '~models/paymentMethods';
import SectionTitle from '~components/SectionTitle';

import { HARDCODED_PEYA_MAX_CASH_AMOUNT, withLogo } from './constants';
import PaymentMethod from './components/PaymentMethod';
import { usePaymentMethodValidation } from './hooks';

function PaymentMethodList({
  paymentMethods,
  onSelectPaymentMethod,
  selectedPaymentMethod,
  setFieldValue,
  isHotspot,
  isPapaCar,
  requestedTimeDispatch,
  dispatchMethod,
  closeModal,
  changingActive,
  compactStyle
}) {
  const validatePaymentMethod = usePaymentMethodValidation({
    paymentMethods,
    isHotspot,
    isPapaCar,
    requestedTimeDispatch
  });

  const getPaymentMethodComponent = ({ paymentMethod, disabledPayment, paymentKey }) => (
    <PaymentMethod
      key={paymentMethod?.name || paymentKey}
      name={paymentMethod?.name || paymentKey}
      originalName={paymentMethod?.originalName}
      selectedPaymentMethod={selectedPaymentMethod}
      iconClassName={paymentMethod?.icon || PAYMENT_METHODS_INFO.default.icon}
      onlinePayment={paymentMethod?.isOnline}
      onSelectPaymentMethod={onSelectPaymentMethod}
      setFieldValue={setFieldValue}
      onlyLogo={!!withLogo[paymentMethod?.name]}
      disabled={!!disabledPayment}
      tooltipMessage={isString(disabledPayment) && disabledPayment}
      dispatchMethod={dispatchMethod}
      closeModal={closeModal}
      changingActive={changingActive}
      compactStyle={compactStyle}
    />
  );

  useEffect(() => {
    if (!paymentMethods) {
      return;
    }

    const paymentMethodCookie = checkoutPaymentMethodViaCookie();
    if (paymentMethodCookie) {
      const paymentKey = Object.keys(paymentMethods)?.find(
        item =>
          PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID][item]?.name === paymentMethodCookie && paymentMethods[item]
      );
      const paymentMethodObject = PAYMENT_METHODS_INFO[CURRENT_COUNTRY_ID][paymentKey];
      if (paymentMethodObject) {
        const { name: paymentMethod, isOnline: onlinePayment } = paymentMethodObject;
        if (!onlinePayment && isHotspot) {
          return;
        }
        if (paymentMethod === PAYMENT_METHODS.powertranzGuest) {
          return;
        }
        if (paymentMethod === PAYMENT_METHODS.mercantilGuest) {
          return;
        }
        onSelectPaymentMethod({ paymentMethod, onlinePayment });
      }
    }
  }, []);

  const getPaymentMethodsComponents = () =>
    Object.keys(paymentMethods)
      .sort(a => (a === PAYMENT_METHODS_SERVER.mercadopago ? -1 : 1))
      .map(paymentKey => {
        const { render: shouldRender, disabled: disabledPayment } = validatePaymentMethod(paymentKey);
        const paymentMethod = getPaymentMethod(paymentKey);
        const extraPaymentMethods =
          CURRENT_COUNTRY_ID === COUNTRIES.es && paymentMethod?.name === PAYMENT_METHODS.directClickToPay
            ? {
                [PAYMENT_METHODS.edenred]: getPaymentMethod(PAYMENT_METHODS_SERVER.edenred),
                [PAYMENT_METHODS.sodexo]: getPaymentMethod(PAYMENT_METHODS_SERVER.sodexo)
              }
            : null;
        if (
          paymentMethod?.name === selectedPaymentMethod &&
          [PAYMENT_METHODS.card, PAYMENT_METHODS.cash].includes(paymentMethod?.name) &&
          !shouldRender
        ) {
          onSelectPaymentMethod({ paymentMethod: null });
        }
        return (
          shouldRender && (
            <>
              {getPaymentMethodComponent({ paymentMethod, disabledPayment, paymentKey })}
              {!!extraPaymentMethods && (
                <>
                  {getPaymentMethodComponent({
                    paymentMethod: extraPaymentMethods.edenred,
                    disabledPayment
                  })}
                  {getPaymentMethodComponent({
                    paymentMethod: extraPaymentMethods.sodexo,
                    disabledPayment
                  })}
                </>
              )}
            </>
          )
        );
      });
  const renderNoCashText =
    Object.keys(paymentMethods).includes(PAYMENT_METHODS.cash) &&
    validatePaymentMethod(PAYMENT_METHODS.cash)?.shouldRenderNoCashWarning;

  const infoCountryPaymentMethods =
    CURRENT_COUNTRY_ID === COUNTRIES.es || CURRENT_COUNTRY_ID === COUNTRIES.pt;

  return (
    <div className={cn('column full-width', { 'm-bottom-2': !compactStyle })}>
      {infoCountryPaymentMethods && (
        <small className="text-dusty-gray m-bottom-2">{t('CheckoutPaymentMethodList:info3DS')}</small>
      )}
      <SectionTitle
        hideSeparator
        className="subtitle-small"
        title={t('CheckoutPaymentMethodList:available')}
        titleClassName={styles.sectionTitle}
      />
      <div
        className={cn(`row wrap ${styles.paymentMethodsContainer}`, {
          [styles.paymentMobile]: renderNoCashText || infoCountryPaymentMethods,
          [styles.threeColumns]: compactStyle,
          [styles.noPaddingBottom]: compactStyle
        })}>
        {paymentMethods && getPaymentMethodsComponents()}
      </div>
      {renderNoCashText && (
        <div className={styles.warningText}>
          {t('CheckoutPaymentMethodList:noCash', {
            max: HARDCODED_PEYA_MAX_CASH_AMOUNT.toLocaleString(
              process.env.REACT_APP_TRANSLATION_COUNTRY.replace('_', '-')
            )
          })}
        </div>
      )}
    </div>
  );
}

PaymentMethodList.propTypes = {
  isHotspot: bool.isRequired,
  isPapaCar: bool.isRequired,
  setFieldValue: func.isRequired,
  onSelectPaymentMethod: func.isRequired,
  changingActive: bool,
  closeModal: func,
  compactStyle: bool,
  dispatchMethod: string,
  paymentMethods: paymentMethodsPropTypes,
  requestedTimeDispatch: string,
  selectedPaymentMethod: oneOf(Object.keys(PAYMENT_METHODS))
};

export default PaymentMethodList;
